import type { HTMLAttributes } from "react";
import type { IconName } from "../../icons";
import { cn } from "../../lib/cn";
import { Icon } from "../icon";

/** The chip tones of the Service Desk mockups (`mockups/tickets/assets/tickets.css` `.chip-*`). */
export type ChipTone = "info" | "success" | "warning" | "danger" | "accent" | "brand" | "muted" | "outline";
export type ChipSize = "md" | "lg";

/**
 * Semantic-token classes per tone. Every pairing is >= 4.5:1 in light and dark (`chip-contrast.test.ts` reads
 * `tokens.css` and checks it). `info` and `brand` darken their ink 18 % in the light theme, as the mockup does,
 * because the plain `--info` ink on `--info-soft` is 4.44:1. `muted` uses `fg-muted`, not the mockup's `fg-subtle` (3.5:1).
 */
export const CHIP_TONE_CLASS: Readonly<Record<ChipTone, string>> = {
  info: "bg-info-soft text-[color:color-mix(in_srgb,var(--info),black_18%)] dark:text-info",
  success: "bg-success-soft text-success",
  warning: "bg-warning-soft text-warning",
  danger: "bg-danger-soft text-danger",
  accent: "bg-accent-soft text-accent-ink",
  brand: "bg-primary-soft text-[color:color-mix(in_srgb,var(--primary-ink),black_18%)] dark:text-primary-ink",
  muted: "bg-sunken text-fg-muted",
  outline: "border-line-strong bg-transparent text-fg-muted",
};

const SIZE_CLASS: Readonly<Record<ChipSize, string>> = {
  md: "h-5.5 gap-1.5 px-2 text-[11px]",
  lg: "h-7 gap-1.5 px-3 text-xs",
};

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: ChipTone;
  size?: ChipSize;
  icon?: IconName;
}

/** Pill with an optional leading icon. Status chips are ALWAYS icon + text + colour (CLAUDE.md rule 10). */
export function Chip({ tone = "muted", size = "md", icon, className, children, ...rest }: ChipProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-pill border border-transparent font-bold leading-none [&>svg]:size-3.25",
        SIZE_CLASS[size],
        CHIP_TONE_CLASS[tone],
        className,
      )}
      data-tone={tone}
      {...rest}
    >
      {icon ? <Icon name={icon} /> : null}
      {children}
    </span>
  );
}
